import React, { useState, useRef, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { Box, IconButton, Tooltip } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ChatIcon from '@mui/icons-material/Chat';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import PersonIcon from '@mui/icons-material/Person';

const pulse = keyframes`
  0% {
    transform: scale(1.2);
  }
  50% {
    transform: scale(1.3);
  }
  100% {
    transform: scale(1.2);
  }
`;

const glow = keyframes`
  0% {
    box-shadow: 0 0 5px rgba(0, 192, 127, 0.5);
  }
  50% {
    box-shadow: 0 0 15px rgba(0, 192, 127, 0.8);
  }
  100% {
    box-shadow: 0 0 5px rgba(0, 192, 127, 0.5);
  }
`;

const NavContainer = styled.div`
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  background-color: #333;
  border-radius: 30px;
  padding: 8px 16px;
  z-index: 10;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  width: 90%;
  max-width: 400px;
  justify-content: space-between;
  cursor: pointer;
  transition: background-color 0.3s;
  
  &:hover {
    background-color: #444;
  }
  
  &.active {
    animation: ${glow} 2s infinite;
    box-shadow: 0 0 10px rgba(0, 192, 127, 0.7);
  }
`;

const NavIconButton = styled(IconButton)`
  color: white;
  padding: 8px;
  ${props => props.active && `
    background-color: rgba(255, 255, 255, 0.2) !important;
  `}
`;

const JJMuseButton = styled(IconButton)`
  background-color: ${props => {
    if (props.disabled) return '#999999';
    if (props.isHolding) return '#9c27b0';
    return '#00C07F';
  }} !important;
  color: white;
  padding: 12px;
  transform: scale(1.2);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  animation: ${props => {
    if (props.isHolding) return css`${pulse} 0.25s infinite`;
    if (props.isTemporarilyEnabled) return css`${glow} 2s infinite`;
    return 'none';
  }};
  transition: background-color 0.3s, opacity 0.3s;
  opacity: ${props => props.disabled ? 0.5 : 1};
  pointer-events: ${props => props.disabled ? 'none' : 'auto'};
  &:hover {
    background-color: ${props => {
      if (props.disabled) return '#999999';
      if (props.isHolding) return '#7B1FA2';
      return '#00A06A';
    }} !important;
  }
`;

const JJMuseIcon = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 20px;
  transform: rotate(10deg);
  color: white;
`;

const CountdownIndicator = styled.div`
  position: absolute;
  top: -20px;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;
  pointer-events: none;
`;

const Navigation = ({ onNavigate, preventAccidentalTrigger, currentView, onNavBarClick }) => {
  const [isHolding, setIsHolding] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const holdTimerRef = useRef(null);
  const modalTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);
  const holdDuration = 250; // 250ms = 1/4 second for visual feedback (color change and pulsing)
  const modalLaunchDuration = 1000; // 1000ms = 1 second before launching the modal

  // Update countdown timer when preventAccidentalTrigger changes
  useEffect(() => {
    if (!preventAccidentalTrigger) {
      // Start countdown from 3 seconds
      setCountdown(3);
      
      // Update countdown every second
      countdownTimerRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownTimerRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      // Clear countdown when prevention is re-enabled
      setCountdown(0);
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
      }
    }
    
    // Cleanup on unmount
    return () => {
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
      }
    };
  }, [preventAccidentalTrigger]);

  const handleMouseDown = () => {
    // Don't process if JJMuse button should be prevented from accidental triggers
    if (preventAccidentalTrigger) return;

    // Timer for visual feedback (color change and pulsing)
    holdTimerRef.current = setTimeout(() => {
      setIsHolding(true);
      console.log('Holding state activated, color should be purple');
    }, holdDuration);
    
    // Separate timer for launching the modal
    modalTimerRef.current = setTimeout(() => {
      console.log('Modal launch timer completed');
      onNavigate('jjmuse');
    }, modalLaunchDuration);
  };

  const handleMouseUp = () => {
    clearTimeout(holdTimerRef.current);
    clearTimeout(modalTimerRef.current);
    setIsHolding(false);
  };

  const handleTouchStart = () => {
    // Don't process if JJMuse button should be prevented from accidental triggers
    if (preventAccidentalTrigger) return;

    // Timer for visual feedback (color change and pulsing)
    holdTimerRef.current = setTimeout(() => {
      setIsHolding(true);
    }, holdDuration);
    
    // Separate timer for launching the modal
    modalTimerRef.current = setTimeout(() => {
      onNavigate('jjmuse');
    }, modalLaunchDuration);
  };

  const handleTouchEnd = () => {
    clearTimeout(holdTimerRef.current);
    clearTimeout(modalTimerRef.current);
    setIsHolding(false);
  };

  const handleNavigate = (route) => {
    // Only navigate if not trying to go to jjmuse when it should be prevented
    if (route === 'jjmuse' && preventAccidentalTrigger) {
      console.log('JJMuse navigation prevented - click navigation bar first');
      return;
    }
    onNavigate(route);
  };

  const handleNavContainerClick = () => {
    // Notify parent component that nav bar was clicked
    if (onNavBarClick) {
      onNavBarClick();
    }
  };

  return (
    <>
      <NavContainer 
        onClick={handleNavContainerClick}
        className={!preventAccidentalTrigger ? 'active' : ''}
      >
        <NavIconButton 
          active={currentView === 'location'} 
          onClick={() => handleNavigate('location')}
        >
          <LocationOnIcon style={{ color: 'white' }} />
        </NavIconButton>
        
        <NavIconButton 
          active={currentView === 'messages'} 
          onClick={() => handleNavigate('messages')}
        >
          <ChatIcon style={{ color: 'white' }} />
        </NavIconButton>
        
        <Tooltip title={preventAccidentalTrigger ? "Click navigation bar to enable JJ Muse" : "JJ Muse is ready!"}>
          <span> {/* Wrapper needed for disabled buttons with tooltips */}
            <JJMuseButton 
              isHolding={isHolding}
              disabled={preventAccidentalTrigger}
              isTemporarilyEnabled={!preventAccidentalTrigger}
              onClick={() => handleNavigate('jjmuse')}
              onMouseDown={handleMouseDown}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
              aria-disabled={preventAccidentalTrigger}
            >
              <img 
                src="/JustJamIcon.png" 
                alt="JustJam" 
                style={{ 
                  width: '30px', 
                  height: '30px', 
                  opacity: preventAccidentalTrigger ? 0.5 : 1,
                  transition: 'opacity 0.3s'
                }} 
              />
            </JJMuseButton>
          </span>
        </Tooltip>
        
        <NavIconButton 
          active={currentView === 'music'} 
          onClick={() => handleNavigate('music')}
        >
          <MusicNoteIcon style={{ color: 'white' }} />
        </NavIconButton>
        
        <NavIconButton 
          active={currentView === 'profile'} 
          onClick={() => handleNavigate('profile')}
        >
          <PersonIcon style={{ color: 'white' }} />
        </NavIconButton>
      </NavContainer>
      
      {!preventAccidentalTrigger && countdown > 0 && (
        <CountdownIndicator>
          JJ Muse enabled for {countdown}s
        </CountdownIndicator>
      )}
    </>
  );
};

export default Navigation;
